import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Polyline, Tooltip, ZoomControl } from "react-leaflet";
import { useLazyQuery } from "@apollo/client";
import { GET_SHIPMENT_TRACKING_COORDS } from "../../queries/getShipmentTrackingCoords";
import "leaflet/dist/leaflet.css";
import Loader from "../Loader/Loader";
import TripDetails, { removeZFromTimestamp } from "../../components/TripDetails/TripDetails";
import Routing from "./components/MapRouting";
import { OlaTileLayer } from "./components/TileLayer";
import Error from "../Error/Error";
import MarkerClusterGroup from "react-leaflet-cluster";
import { FullscreenControl } from "./components/FullScreenControl";
import { simIcon, appIcon, fastTagIcon, iconDes, iconNav, iconOrg, createCustomIcon } from "./components/Ions";
import moment from "moment";
import { CenterToLastKnownLocationButton } from "./components/LastLocationButton";
import SettingsButton from "./components/SettingsButton";
import "./TrackingMap.scss"; // Import the SCSS
import { ErrorToast, WarningToast } from "../../../../ToastComponent/RectHotToast";
import RoadRouting from "./components/RoadRouting";
import { filterPositions } from "./ExtraFunctions";

const TrackingMap = ({ trackID }) => {
  const mapRef = useRef(null); // Create a ref for the map instance
  const defaultCenter = [20.5937, 78.9629]; // Example: coordinates for India
  const [center, setCenter] = useState(defaultCenter);
  const [settings, setSettings] = useState({
    "Connect Locations": false,
    Cluster: false,
    "Recommended Route": true,
    "Location Counter": false,
    "Connect Points": false,
  });
  const [visibility, setVisibility] = useState({
    Sim: true,
    FastTag: true,
    App: true,
  });

  const [loadShipmentTracking, { loading, data, error }] = useLazyQuery(GET_SHIPMENT_TRACKING_COORDS, {
    onCompleted: (data) => {
      const lastLocation = data?.GetTrackShipmentByIdWithLocationData?.Location_Data?.Last_Known_Location;
      if (lastLocation) {
        setCenter([lastLocation.latitude || 20.5937, lastLocation.longitude || 78.9629]);
      }
    },
    onError: (err) => {
      ErrorToast(err.message);
    },
    context: {
      headers: { "X-GraphQL-Server": "location" },
    },
    fetchPolicy: "no-cache",
  });
  const toggleVisibility = (type) => {
    setVisibility((prev) => ({ ...prev, [type]: !prev[type] }));
  };
  const handleOptionChange = (optionLabel) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [optionLabel]: !prevSettings[optionLabel],
    }));
  };

  useEffect(() => {
    loadShipmentTracking({ variables: { tripid: trackID } });
  }, [loadShipmentTracking]);

  const trackingData = data?.GetTrackShipmentByIdWithLocationData;
  console.log(trackingData, "trackingData");
  const origin = useMemo(() => {
    const lat = trackingData?.Location_Data?.Origin?.latitude;
    const lon = trackingData?.Location_Data?.Origin?.longitude;
    return lat !== undefined && lon !== undefined ? [lat, lon] : null;
  }, [trackingData]);

  const destination = useMemo(() => {
    const lat = trackingData?.Location_Data?.Destination?.latitude;
    const lon = trackingData?.Location_Data?.Destination?.longitude;
    return lat !== undefined && lon !== undefined ? [lat, lon] : null;
  }, [trackingData]);

  const lastKnownLocation = useMemo(() => {
    const lat = trackingData?.Location_Data?.Last_Known_Location?.latitude;
    const lon = trackingData?.Location_Data?.Last_Known_Location?.longitude;
    return lat !== undefined && lon !== undefined ? [lat, lon] : null;
  }, [trackingData]);

  const getPositions = (type) => {
    return (
      trackingData?.Location_Data?.[`${type}_History`]?.map((location) => {
        return {
          pos: [location.latitude, location.longitude],
          address: location.address,
          PingNumber: location.PingNumber,
          eta: moment(location.recorded_at).format("DD/MM/YYYY (hh:mm A)"),
          recorded_at: location.recorded_at,
          Source: type,
        };
      }) || []
    );
  };

  const renderMarkersAndPolylines = (positions, icon, type, color) => {
    if (!visibility[type] || positions.length === 0) return null;

    const markers = positions.map((locationData, idx) => {
      const [lat, lon] = locationData.pos;
      if (!lat || !lon) return null; // Skip if coordinates are invalid

      const counterHtml = settings["Location Counter"]
        ? `<div style="background-color: ${color};" class="location-counter-container">${idx + 1}</div>`
        : "";

      const lineHtml = settings["Location Counter"]
        ? `<div class="location-line" style="border-top: 2px solid ${color};"></div>` // Horizontal line
        : "";

      const customIcon = createCustomIcon(`
        <div class="custom-icon-location-marker">
          <div class="marker-icon" style="background-color: ${color}; width: 10px; height: 10px; border-radius: 50%;"></div>
          ${lineHtml}
          ${counterHtml}
        </div>
      `);

      return (
        <Marker key={idx} position={locationData.pos} icon={customIcon}>
          <Tooltip
            permanent={false}
            interactive={true}
            className="bg-transparent border-none" // Custom class for additional styling
          >
            <div className="trip-details-tooltip-container">
              <div className="trip-details-tooltip-header">
                <span className="trip-details-tooltip-ping">Ping {locationData.PingNumber || idx + 1}</span>
                <span className="trip-details-tooltip-source">{locationData.Source}</span>
              </div>
              <div className="trip-details-tooltip-eta">{locationData.eta}</div>
              <div className="trip-details-tooltip-address">{locationData.address}</div>
            </div>
          </Tooltip>
        </Marker>
      );
    });

    // Generate polyline to connect points
    const polylinePositions = positions.map((location) => location.pos);

    return (
      <>
        {settings["Cluster"] ? <MarkerClusterGroup>{markers}</MarkerClusterGroup> : markers}
        {settings["Connect Points"] && <Polyline positions={polylinePositions} color={color} />}
      </>
    );
  };

  const simPositions = getPositions("Sim");
  const fastTagPositions = getPositions("FastTag");
  const appPositions = getPositions("APP");
  let AllPings = useMemo(() => {
    const App_Pos = visibility.App ? filterPositions(appPositions) : [];
    const sim_pos = visibility.Sim ? simPositions : [];
    const fast_pos = visibility.FastTag ? fastTagPositions : [];
    const data = [...sim_pos, ...fast_pos, ...App_Pos];
    const sortedData = data.sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at)).map((el) => el.pos);
    return sortedData;
  }, [simPositions, fastTagPositions, appPositions, visibility]);
  // console.log(AllPings, "AllPingsAllPingsAllPingsAllPingsAllPings")
  if (loading) return <Loader />;
  if (error) return <Error message={error.message} />;

  return (
    <div className="tracking-map">
      <div className="map-container">
        {data?.GetTrackShipmentByIdWithLocationData && (
          <MapContainer
            center={center}
            zoom={6}
            zoomControl={false}
            ref={mapRef}
            whenCreated={(mapInstance) => {
              mapRef.current = mapInstance;
            }}
          >
            <OlaTileLayer />
            {renderMarkersAndPolylines(simPositions, simIcon, "Sim", "#108200")}
            {renderMarkersAndPolylines(fastTagPositions, fastTagIcon, "FastTag", "#2363F2")}
            {renderMarkersAndPolylines(appPositions, appIcon, "App", "#FE8100")}
            {settings["Recommended Route"] && <Routing origin={origin} destination={destination} />}
            {settings["Connect Locations"] && <RoadRouting waypoints={AllPings} color={"#2363F2"} />}
            <FullscreenControl />
            {origin && <Marker position={origin} icon={iconOrg} />}
            {lastKnownLocation && (
              <Marker position={lastKnownLocation} icon={iconNav}>
                <Tooltip permanent={false} interactive={true} className="trip_tooltip">
                  <div className="trip_tooltip_content">
                    <div className="trip_tooltip_header">
                      <span className="ping_number">
                        Ping {trackingData?.Location_Data?.Last_Known_Location?.PingNumber}
                      </span>
                      <span className="source_info">{trackingData?.Location_Data?.Last_Known_Location?.Source}</span>
                    </div>
                    <div className="eta_info">
                      {moment(removeZFromTimestamp(trackingData.Location_Data.Last_Known_Location.recorded_at)).format(
                        "DD/MM/YYYY (hh:mm A)"
                      ) || "-"}
                    </div>
                    <div className="address_info">{trackingData?.Location_Data?.Last_Known_Location?.address}</div>
                  </div>
                </Tooltip>
              </Marker>
            )}
            {destination && <Marker position={destination} icon={iconDes} />}
            <ZoomControl position="bottomright" />
          </MapContainer>
        )}
        <div className="map-controls" id="top-on-map">
          <button className={visibility.Sim ? "sim-active" : "sim-inactive"} onClick={() => toggleVisibility("Sim")}>
            SIM
          </button>
          <button
            className={visibility.FastTag ? "fasttag-active" : "fasttag-inactive"}
            onClick={() => toggleVisibility("FastTag")}
          >
            FastTag
          </button>
          <button className={visibility.App ? "app-active" : "app-inactive"} onClick={() => toggleVisibility("App")}>
            App
          </button>
        </div>
        <div className="right-controls">
          <CenterToLastKnownLocationButton mapRef={mapRef} position={center} />
          <SettingsButton
            options={[
              { label: "Connect Locations", checked: settings["Connect Locations"] },
              { label: "Cluster", checked: settings["Cluster"] },
              { label: "Recommended Route", checked: settings["Recommended Route"] },
              { label: "Location Counter", checked: settings["Location Counter"] },
              { label: "Connect Points", checked: settings["Connect Points"] },
            ]}
            onOptionChange={handleOptionChange}
          />
        </div>
      </div>
      <TripDetails trackingData={trackingData} />
    </div>
  );
};

export default TrackingMap;
